import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { mockGenerate, EXAMPLE_PROMPTS } from '@/data/mockGenerate'
import ManualBuilder, { clearBuilderDraft } from './builder/ManualBuilder'
import ImportPanel from './builder/ImportPanel'
import { AssignDialog } from './builder/AssignDialog'
import { useSaveTemplate, useProgramById, useUpdateProgram } from '@/hooks/usePrograms'
import type { Program } from '@/types'

type Tab = 'ai' | 'import' | 'manual'

const TAB_LABELS: Record<Tab, string> = {
  ai: '✨ Generate with AI',
  import: '⬆ Import',
  manual: '✍ Build manually',
}

export default function ProgramBuilder() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>(() => {
    const p = new URLSearchParams(window.location.search)
    if (p.get('edit')) return 'manual'
    // Resume a manual draft in progress (survives tab switches / reloads).
    if (localStorage.getItem('fz-builder-draft-new')) return 'manual'
    return 'ai'
  })
  const [prompt, setPrompt] = useState('')
  const [generating, setGenerating] = useState(false)
  // Program loaded from AI or Import → opened in the editable builder.
  const [loaded, setLoaded] = useState<Program | null>(null)
  const [assigning, setAssigning] = useState<Program | null>(null)

  const [savedMsg, setSavedMsg] = useState<string | null>(null)
  const saveTemplate = useSaveTemplate()
  const updateProgram = useUpdateProgram()

  // Edit an existing program in place: /coach/builder?edit=<programId>[&back=<path>]
  const [params] = useSearchParams()
  const editId = params.get('edit') ?? ''
  const backTo = params.get('back') ?? '/coach'
  const assignAthleteId = params.get('assign') ?? ''
  const editQ = useProgramById(editId, Boolean(editId))

  const openInEditor = (p: Program) => {
    setLoaded(p)
    setTab('manual')
  }

  // AI / import produce a fresh program → clear any stale blank draft so the
  // new program seeds cleanly (then it auto-saves under the same key).
  const loadFresh = (p: Program) => {
    localStorage.removeItem('fz-builder-draft-new')
    openInEditor(p)
  }

  useEffect(() => {
    if (editQ.data) openInEditor(editQ.data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editQ.data])

  const draftKey = editId ? 'edit-' + editId : 'new'

  const saveAsTemplate = (p: Program) => {
    saveTemplate.mutate(p, {
      onSuccess: () => {
        clearBuilderDraft(draftKey)
        setSavedMsg('Saved to your templates.')
        setTimeout(() => navigate('/coach/templates'), 700)
      },
    })
  }

  const saveChanges = (p: Program) => {
    updateProgram.mutate(
      { id: editId, program: p },
      {
        onSuccess: () => {
          clearBuilderDraft(draftKey)
          setSavedMsg('Changes saved.')
          setTimeout(() => navigate(backTo), 700)
        },
      }
    )
  }

  const generate = () => {
    if (!prompt.trim()) return
    setGenerating(true)
    // Simulates the round-trip to the ai-proxy → Claude. Real call in Phase 5.
    setTimeout(() => {
      loadFresh(mockGenerate(prompt))
      setGenerating(false)
    }, 1100)
  }

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gold/25 bg-surface/95 px-5 py-3 backdrop-blur">
        <button onClick={() => navigate(editId ? backTo : '/coach')} className="text-gold hover:-translate-x-0.5" aria-label="Back">
          ‹
        </button>
        <h1 className="font-display text-xl tracking-wide">{editId ? 'Edit Program' : 'New Program'}</h1>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-5 pb-20 pt-6">
        {/* Tabs (hidden when editing an existing program) */}
        <div className={cn('mb-6 inline-flex flex-wrap gap-1 rounded-full border border-border bg-surface p-1', editId && 'hidden')}>
          {(['ai', 'import', 'manual'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'rounded-full px-4 py-1.5 font-condensed text-sm font-semibold uppercase tracking-wide transition-all',
                tab === t ? 'gold-gradient text-black' : 'text-muted hover:text-text'
              )}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>

        {tab === 'manual' && (
          <>
            {loaded && !editId && (
              <p className="mb-3 rounded-btn border border-data/30 bg-data/10 px-4 py-2.5 text-sm text-data">
                Loaded from {loaded.source === 'import' ? 'your spreadsheet' : 'AI'} — edit anything below
                (tap an exercise name to swap it), then Save &amp; Assign.
              </p>
            )}
            {editId && (
              <p className="mb-3 rounded-btn border border-gold/30 bg-gold/10 px-4 py-2.5 text-sm text-gold">
                Editing this program — changes save straight to the athlete. Tap an exercise name to swap it.
              </p>
            )}
            {(saveTemplate.isError || updateProgram.isError) && (
              <p className="mb-3 rounded-btn border border-danger/30 bg-danger/10 px-4 py-2.5 text-sm text-danger">
                Couldn’t save — {((saveTemplate.error || updateProgram.error) as Error)?.message ?? 'please try again'}.
                Your work is still here; try again.
              </p>
            )}
            {savedMsg && (
              <p className="mb-3 rounded-btn border border-success/30 bg-success/10 px-4 py-2.5 text-sm text-success">
                {savedMsg}
              </p>
            )}
            {/* When editing, wait for the program to load so the editor seeds
                from it (mounting early would show a blank form). */}
            {editId && !loaded ? (
              <p className="py-10 text-center text-muted">
                {editQ.isError ? 'Couldn’t load this program.' : 'Loading program…'}
              </p>
            ) : (
              <ManualBuilder
                key={editId || loaded?.id || 'blank'}
                initial={loaded ?? undefined}
                draftKey={draftKey}
                onSave={editId ? undefined : (p) => setAssigning(p)}
                onUpdate={editId ? saveChanges : undefined}
                onSaveTemplate={saveAsTemplate}
              />
            )}
          </>
        )}
        {tab === 'import' && <ImportPanel onLoaded={loadFresh} />}

        {tab === 'ai' && (
          <>
            <label className="mb-2 block font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">
              Describe the program you want
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. 6-week ACL return-to-play for a netball athlete, week 3, avoid deep loaded knee flexion, 3 sessions a week, we have a leg press and cable machine…"
              className="h-32 w-full resize-none rounded-input border border-border-strong bg-white/[.03] p-4 text-[15px] leading-relaxed text-text outline-none placeholder:text-subtle focus:border-gold focus:ring-2 focus:ring-gold/25"
            />

            <div className="mt-3 flex flex-wrap gap-2">
              {EXAMPLE_PROMPTS.map((ex) => (
                <button
                  key={ex}
                  onClick={() => setPrompt(ex)}
                  className="rounded-full border border-border bg-surface px-3 py-1.5 text-xs text-muted transition-colors hover:border-gold/50 hover:text-gold"
                >
                  {ex}
                </button>
              ))}
            </div>

            <Button block size="lg" className="mt-5" disabled={!prompt.trim() || generating} onClick={generate}>
              {generating ? 'Generating…' : '✨ Generate Program'}
            </Button>

            {generating && <GeneratingSkeleton />}
          </>
        )}
      </main>

      {assigning && (
        <AssignDialog
          onAssigned={() => clearBuilderDraft(draftKey)}
          program={assigning}
          preselectId={assignAthleteId || undefined}
          onClose={() => setAssigning(null)}
        />
      )}
    </div>
  )
}

function GeneratingSkeleton() {
  return (
    <div className="mt-6 space-y-3">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-20 animate-pulse rounded-card border border-border bg-surface-2"
          style={{ animationDelay: `${i * 120}ms` }}
        />
      ))}
    </div>
  )
}
